import React, { useEffect, useMemo, useState } from 'react';
import {
  Flex,
  Text,
  Tooltip,
  Box,
  Skeleton,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Divider,
  Toast
} from '@chakra-ui/react';
import {
  faBarsFilter,
  faDeleteRight,
  faFloppyDisk,
  faSearch,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  ButtonText,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  SearchInput
} from '@morressier/alembic';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IMySubmissionFilter } from 'src/api/queries';
import ListLoader from 'src/components/ListLoader';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import { useForm } from 'react-hook-form';
import {
  parseSubmissionQueryParams,
  updateQueryParams
} from 'src/utils/submissionsList';
import SubmissionLoader from '../Organization/Workspace/Submissions/SubmissionLoader';
import {
  IAuthor,
  ISavedFilterSets,
  ISubmissionDraftItem,
  ISubmissionWorkflowState
} from '@publishing-engine/domain/models';
import { Helmet } from 'react-helmet';
import {
  useSubmissionDashBoardConfig,
  useSubmissions,
  defaultValues,
  useSavedFilters
} from './hooks';
import FilterDrawer from '../Organization/public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import SortMenu, {
  SortField
} from '../Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';
import { SubmissionAuthors } from '../Organization/Workspace/Submissions/SubmissionAuthors';
import ManageSavedFiltersModal from '../Organization/public/Journal/SubmissionUpload/components/common/Filters/ManageSavedFiltersModal';
import SaveFiltersModal from '../Organization/public/Journal/SubmissionUpload/components/common/Filters/SaveFilterModal';
import SaveFilterText from '../Organization/Workspace/Submissions/SaveFilterText';
import { formatDate, formatDateAndTime } from 'src/utils/date';

const DraftSubmissions = () => {
  const { watch, reset, resetField, setValue, getValues } =
    useForm<IMySubmissionFilter>({
      defaultValues: {
        ...defaultValues,
        submissionType: 'my-submissions-draft'
      }
    });
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const [sort, setSort] = useState<SortField | null>(null);
  const [filterName, setFilterName] = useState<string | null>(null);
  const [showSaveFiltersModal, setShowSaveFiltersModal] =
    useState<boolean>(false);
  const [showManageFiltersModal, setShowManageFiltersModal] =
    useState<boolean>(false);
  const filters = watch();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: dashBoardConfigs } = useSubmissionDashBoardConfig(
    'my-submissions-draft'
  );

  const { data: savedFilters, isLoading: isSavedFiltersLoading } =
    useSavedFilters('my-submissions-draft');

  const offset = (page - 1) * pageSize;
  const { data, isLoading, isFetching, isFetchingNextPage } =
    useSubmissions<ISubmissionDraftItem>(
      'draft',
      page,
      offset,
      filters,
      sort ?? undefined,
      pageSize
    );

  const clearFilters = () => {
    reset({ ...defaultValues, submissionType: 'my-submissions-draft' });
    setSearchValue('');
    setSelectedFilters({});
    navigate({
      pathname: location.pathname,
      search: '?submissionType=my-submissions-draft'
    });
  };

  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        journalId: filters.journalId ?? [],
        workflowPhase: filters.workflowPhase ?? [],
        searchParams: (filters.searchParams ?? '').trim()
      }),
    [filters.journalId, filters.workflowPhase, filters.searchParams]
  );
  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  // Parse query parameters on initial load
  useEffect(() => {
    const { filters: newFilters, sort: newSort } = parseSubmissionQueryParams(
      location.search
    );
    if (newFilters.submissionType === 'my-submissions-draft') {
      setSearchValue(newFilters.searchParams);
      reset(newFilters);
      setSelectedFilters({
        journalId: newFilters.journalId ?? [],
        workflowPhase: newFilters.workflowPhase ?? []
      });
      setSort(newSort);
    }
  }, [reset]);

  // Sync URL query params to formState
  useEffect(() => {
    updateQueryParams(
      {
        ...filters,
        sort: sort ? `${sort.label},${sort.value},${sort.direction}` : ''
      },
      location,
      navigate
    );
  }, [filters, sort, navigate]);
  const total = data?.pages?.[data.pages.length - 1]?.total ?? 0;
  const hasFilters =
    !!filters.workflowPhase.length ||
    !!filters.journalId.length ||
    !!filters.searchParams;

  const handleFiltersSubmit = (selectedFilters: Record<string, string[]>) => {
    Object.entries(selectedFilters).forEach(([key, values]) => {
      setValue(key as keyof IMySubmissionFilter, values as any);
    });
  };

  const startIndex = offset + 1;
  const endIndex = Math.min(offset + pageSize, total);

  const handleSortApply = (selected: SortField | null) => {
    setSort(selected);
    setPage(1);
  };

  const handleSortReset = () => {
    setSort(null);
    setPage(1);
  };

  const removeSingleFilter = (
    filterToRemove: string,
    fieldName: keyof IMySubmissionFilter
  ) => {
    const currentFilter = getValues(fieldName) as string[];
    const updatedFilter = currentFilter.filter(
      filter => filter !== filterToRemove
    );
    setValue(fieldName, updatedFilter, {
      shouldValidate: true,
      shouldDirty: true
    });
    setSelectedFilters(prev => ({
      ...prev,
      [fieldName]: updatedFilter
    }));
    setFilterName(null);
  };

  const handleSetValues = (data: ISavedFilterSets, name: string) => {
    setValue('searchParams', data?.searchParams ?? '');
    setValue(
      'workflowPhase',
      (data?.workflowPhase as ISubmissionWorkflowState[]) ?? []
    );
    setValue('journalId', data?.journalId ?? []);
    setFilterName(name);
    setSearchValue(data?.searchParams ?? '');
    setSelectedFilters({
      workflowPhase:
        (data?.workflowPhase as ISubmissionWorkflowState[])?.map(w => w) ?? [],
      journalId: data?.journalId ?? []
    });
    return Toast({
      variant: 'success',
      title: `${name} Filter Applied`
    });
  };

  const onDeleteClearFilters = () => {
    clearFilters();
    setFilterName(null);
  };

  const handleSaveFiltersModal = (name: string | null) => {
    setShowSaveFiltersModal(prev => !prev);
    if (name) setFilterName(name);
  };

  return (
    <>
      <Flex flexDirection={'column'} gap={4} width="100%">
        <Helmet>
          <title>Drafts</title>
        </Helmet>
        {showSaveFiltersModal ? (
          <SaveFiltersModal
            isOpen={showSaveFiltersModal}
            onClose={handleSaveFiltersModal}
            filter={filters}
            view="my-submissions-draft"
          />
        ) : null}
        {showManageFiltersModal ? (
          <ManageSavedFiltersModal
            isOpen={showManageFiltersModal}
            onClose={() => setShowManageFiltersModal(false)}
            savedFilters={savedFilters?.response}
            availableFilters={dashBoardConfigs?.filters}
            isSavedFiltersLoading={isSavedFiltersLoading}
            clearFilters={onDeleteClearFilters}
            view={'my-submissions-draft'}
          />
        ) : null}
        <Flex gap={2} flexDirection="column">
          <Flex gap={3} mb={2} direction={{ base: 'column', md: 'row' }}>
            <Flex gap={2} alignItems="center" flex="1">
              <SearchInput
                placeholder="Search submissions by title"
                iconLeft={<FontAwesomeIcon icon={faSearch} />}
                width="100%"
                name="searchParams"
                value={searchValue}
                enterKeyHint="search"
                onChange={e => setSearchValue(e.currentTarget.value)}
                onInputClear={() => {
                  setSearchValue('');
                  resetField('searchParams');
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setValue('searchParams', searchValue.trim());
                    setFilterName(null);
                  }
                }}
                onKeyUp={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setValue('searchParams', searchValue.trim());
                    setFilterName(null);
                  }
                }}
              />
              <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
                <Button
                  variant="primary"
                  aria-label="Search"
                  onClick={() => {
                    setValue('searchParams', searchValue.trim());
                    setFilterName(null);
                  }}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </Button>
              </Box>
            </Flex>
            {dashBoardConfigs?.filters && (
              <FilterDrawer
                filterGroups={dashBoardConfigs.filters.map((filter: any) => ({
                  ...filter,
                  items: filter.items ?? []
                }))}
                watch={watch}
                onSubmit={handleFiltersSubmit}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
              />
            )}
            <Flex width={{ base: '100%', md: 'auto' }}>
              <Menu ariaLabel="filters">
                {({ onClose }) => (
                  <>
                    <MenuButton
                      as={Button}
                      variant="secondary"
                      iconLeft={<FontAwesomeIcon icon={faBarsFilter} />}
                      width={{ base: '100%', md: 'auto' }}
                    >
                      Saved Filters
                    </MenuButton>
                    <MenuList textStyle="headingItem">
                      {savedFilters?.response?.length ? (
                        <>
                          <Box maxH="300px" overflowY="auto" maxW="350px">
                            {savedFilters.response.map(item => (
                              <React.Fragment key={item.id}>
                                <MenuItem
                                  onClick={() => {
                                    const filter =
                                      item.filters as ISavedFilterSets;
                                    handleSetValues(filter, item.name);
                                  }}
                                  ariaLabel={item.name}
                                >
                                  <Text textStyle="headingLine" isTruncated>
                                    {item.name}
                                  </Text>
                                  {
                                    <SaveFilterText
                                      saveFilters={
                                        item.filters as ISavedFilterSets
                                      }
                                      availableFilters={
                                        dashBoardConfigs?.filters
                                      }
                                      manageTags={[]}
                                    />
                                  }
                                </MenuItem>
                                <MenuDivider />
                              </React.Fragment>
                            ))}
                          </Box>
                          <Flex
                            alignItems="center"
                            justifyContent="center"
                            padding={3}
                            position="sticky"
                          >
                            <ButtonText
                              onClick={() => {
                                setShowManageFiltersModal(true);
                                onClose();
                              }}
                            >
                              Manage Filters
                            </ButtonText>
                          </Flex>
                        </>
                      ) : (
                        <Flex
                          flexDirection={'column'}
                          gap={4}
                          padding={8}
                          justifyContent={'center'}
                          alignItems={'center'}
                          maxW="350px"
                        >
                          <Text textStyle="headingGroup">No Saved Filters</Text>
                          <Text textStyle="textBody" textAlign={'center'}>
                            You do not have any saved filters yet. Try applying
                            some filters and then click 'Save Filters' to save
                            them for later usage.
                          </Text>
                        </Flex>
                      )}
                    </MenuList>
                  </>
                )}
              </Menu>
            </Flex>
          </Flex>
        </Flex>
        <Skeleton isLoaded={!isLoading}>
          <Flex
            alignItems={{ base: 'flex-start', lg: 'center' }}
            gap={3}
            height={{ lg: 5 }}
            flexDirection={{ base: 'column', lg: 'row' }}
            justifyContent={'space-between'}
            width="100%"
          >
            <Flex wrap="wrap" alignItems="center">
              <Text textStyle="textBody">
                {total > 0 ? (
                  <>
                    Showing <strong>{startIndex}</strong> -{' '}
                    <strong>{endIndex}</strong> of <strong>{total}</strong>{' '}
                    draft{total === 1 ? '' : 's'}
                  </>
                ) : (
                  <>
                    <strong>0</strong> draft
                  </>
                )}
              </Text>
              {hasFilters && (
                <>
                  {filterName ? (
                    <Box marginX={2}>
                      <Text textStyle="textBody">
                        <strong>{filterName}</strong> applied
                      </Text>
                    </Box>
                  ) : (
                    <ButtonText
                      leftIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
                      variant="secondary"
                      onClick={() => setShowSaveFiltersModal(true)}
                      marginX={{ base: 0, md: 2 }}
                    >
                      Save Filters
                    </ButtonText>
                  )}
                  <ButtonText
                    leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
                    variant="secondary"
                    onClick={() => {
                      clearFilters();
                      setFilterName(null);
                    }}
                  >
                    Clear Filters
                  </ButtonText>
                </>
              )}
            </Flex>
            <Divider
              orientation="horizontal"
              flex="1"
              mx={4}
              borderColor="gray.300"
              display={{ base: 'none', lg: 'block' }}
            />
            {dashBoardConfigs?.sortOptions?.fields && (
              <Box width={{ base: '100%', lg: 'auto' }}>
                <SortMenu
                  appliedSort={sort}
                  options={dashBoardConfigs.sortOptions}
                  onApply={handleSortApply}
                  onReset={handleSortReset}
                />
              </Box>
            )}
          </Flex>
          {!!hasFilters && (
            <Flex alignItems="center" gap={2} wrap="wrap" marginTop={2}>
              {dashBoardConfigs?.filters?.map(filterConfig => {
                // Type guard to check if filterConfig has 'items'
                const hasItems = (fc: any): fc is { items: any[] } =>
                  Array.isArray(fc.items);

                if (
                  !hasItems(filterConfig) ||
                  filterConfig.items.length === 0
                ) {
                  return null;
                }

                const filterKey = filterConfig.key as keyof typeof filters;
                const filterValues = filters?.[filterKey];

                if (!Array.isArray(filterValues) || filterValues.length === 0) {
                  return null;
                }

                return filterValues.map(value => {
                  const item = filterConfig.items.find(
                    (i: any) => i.value === value
                  );
                  const label = item?.name ?? value;

                  return (
                    <Badge
                      key={`${filterKey}-${value}`}
                      size="base"
                      variant="interactive"
                    >
                      <Flex alignItems="center" gap={2}>
                        {label}
                        <FontAwesomeIcon
                          icon={faXmark}
                          cursor="pointer"
                          onClick={() =>
                            removeSingleFilter(
                              value,
                              filterKey as keyof IMySubmissionFilter
                            )
                          }
                        />
                      </Flex>
                    </Badge>
                  );
                });
              })}
            </Flex>
          )}
        </Skeleton>
        <Box width="100%">
          {isLoading || isFetching ? (
            <ListLoader />
          ) : data?.pages[0] && total !== 0 ? (
            <>
              <Box overflowX="auto">
                <Box minWidth={{ base: '860px', md: '100%' }}>
                  <Flex flexDirection="column">
                    {/* @ts-ignore */}
                    <Table size="sm" width="100%">
                      <Thead>
                        <Tr
                          data-testId="submissions-table-header"
                          gap={4}
                          sx={{
                            '& th': {
                              textTransform: 'none',
                              color: 'black',
                              background: 'white'
                            }
                          }}
                        >
                          <Th p={4} style={{ width: '50%' }}>
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Submission Title & Authors
                            </Text>
                          </Th>
                          <Th p={4}>
                            <Text textStyle="headingItem">Created Date</Text>
                          </Th>
                          <Th p={4}>
                            <Text textStyle="headingItem">Last Updated</Text>
                          </Th>
                          <Th p={4}>
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Journal
                            </Text>
                          </Th>
                          <Th p={4}>
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Article Type
                            </Text>
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {data.pages.map(({ items }, i) => (
                          <React.Fragment key={i}>
                            {items.map(
                              (
                                {
                                  submissionId,
                                  submissionContextId,
                                  draft,
                                  journal,
                                  articleType,
                                  draftCreated,
                                  draftUpdated
                                },
                                index
                              ) => {
                                return (
                                  <Tr
                                    key={submissionId}
                                    data-testId="submissions-table-row"
                                    gap={4}
                                    background="white"
                                  >
                                    <Td
                                      onClick={() =>
                                        navigate(
                                          `/j/${submissionContextId}?draftId=${submissionId}`
                                        )
                                      }
                                      cursor="pointer"
                                      _hover={{ bg: 'gray.50' }}
                                    >
                                      <Tooltip
                                        hasArrow
                                        borderRadius={4}
                                        placement="top"
                                        label={'View Draft'}
                                      >
                                        <Box
                                          noOfLines={1}
                                          isTruncated
                                          width="100%"
                                        >
                                          <Link
                                            to={`/j/${submissionContextId}?draftId=${submissionId}`}
                                          >
                                            <Text
                                              textStyle="headingLine"
                                              whiteSpace="normal"
                                              textDecoration="none"
                                              _hover={{
                                                textDecoration: 'underline',
                                                textDecorationColor:
                                                  'actionPrimaryFill'
                                              }}
                                            >
                                              {draft?.metadata?.title ? (
                                                <RichTextPreview
                                                  value={draft.metadata.title}
                                                  color="actionPrimaryFill"
                                                />
                                              ) : (
                                                `Untitled Submission ${index}`
                                              )}
                                            </Text>
                                          </Link>
                                        </Box>
                                      </Tooltip>
                                      <SubmissionAuthors
                                        authors={
                                          draft?.metadata?.authors as IAuthor[]
                                        }
                                      />
                                    </Td>
                                    <Td>
                                      {draftCreated !== null
                                        ? formatDate(
                                            new Date(draftCreated ?? 'N/A')
                                          )
                                        : 'N/A'}
                                    </Td>
                                    <Td>
                                      {draftUpdated
                                        ? formatDateAndTime(draftUpdated)
                                        : 'N/A'}
                                    </Td>

                                    <Td
                                      textStyle="textBody"
                                      textTransform="uppercase"
                                    >
                                      {journal?.shortname}
                                    </Td>
                                    <Td textStyle="textBody"> {articleType}</Td>
                                  </Tr>
                                );
                              }
                            )}
                          </React.Fragment>
                        ))}
                        {isFetchingNextPage && <ListLoader />}
                      </Tbody>
                    </Table>
                  </Flex>
                </Box>
              </Box>
              <TablePagination
                ariaLabel="Submission pagination"
                currentPage={page}
                totalItems={total}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </>
          ) : (
            !isLoading && (
              <SubmissionLoader
                title="You do not have any submissions"
                subTitle="We could not find any results matching your search criteria. Please adjust your search and try again."
                bgColor="neutralSecondaryFill"
              />
            )
          )}
        </Box>
      </Flex>
    </>
  );
};

export default DraftSubmissions;
